import React, {useLayoutEffect, useRef, useState} from 'react'

interface Props {
    name: string
    lore: string
    onReady?: (size: 'small' | 'large') => void
}

type Phase = 'measuring' | 'small' | 'large'
type Segment = {type: 'text'; content: string} | {type: 'oog'; content: string}

function parseLore(lore: string): Segment[] {
    const segments: Segment[] = []
    const regex = /<([^>]+)>/g
    let lastIndex = 0
    let match: RegExpExecArray | null
    while ((match = regex.exec(lore)) !== null) {
        if (match.index > lastIndex) {
            segments.push({type: 'text', content: lore.slice(lastIndex, match.index)})
        }
        segments.push({type: 'oog', content: match[1]})
        lastIndex = regex.lastIndex
    }
    if (lastIndex < lore.length) {
        segments.push({type: 'text', content: lore.slice(lastIndex)})
    }
    return segments.filter(s => s.content.trim())
}

/** Splits a text string on \...\ markers and returns spans, italicizing the marked runs. */
function renderTextWithItalics(text: string): React.ReactNode {
    const parts = text.split('\\')
    return parts.map((part, i) =>
        i % 2 === 1 ? <em key={i}>{part}</em> : part
    )
}

const FONT = '"Palatino Linotype", Palatino, "Book Antiqua", Georgia, serif'
const SANS = 'Arial, Helvetica, sans-serif'
const PARCHMENT_BG = [
    'radial-gradient(ellipse at 10% 15%, rgba(160,120,60,0.18) 0%, transparent 50%)',
    'radial-gradient(ellipse at 90% 85%, rgba(140,100,40,0.18) 0%, transparent 50%)',
    'radial-gradient(ellipse at 85% 10%, rgba(120,90,30,0.12) 0%, transparent 45%)',
    'radial-gradient(ellipse at 15% 90%, rgba(130,100,40,0.12) 0%, transparent 45%)',
    'linear-gradient(160deg, #f9edd4 0%, #f2e0b0 40%, #ecd49a 70%, #e8cc88 100%)',
].join(', ')

export function LoreScrollPrint({name, lore, onReady}: Props) {
    const [phase, setPhase] = useState<Phase>('measuring')
    const containerRef = useRef<HTMLDivElement>(null)

    useLayoutEffect(() => {
        if (phase === 'measuring') {
            const el = containerRef.current
            if (el && el.scrollHeight > el.clientHeight) {
                setPhase('large')
            } else {
                setPhase('small')
            }
        } else {
            onReady?.(phase)
        }
    }, [phase])

    const isLarge = phase === 'large'
    const segments = parseLore(lore)

    return (
        <div
            ref={containerRef}
            style={{
                width:  isLarge ? '11in'   : '5.5in',
                height: isLarge ? '8.5in'  : '8.5in',
                margin: 0,
                padding: 0,
                boxSizing: 'border-box',
                overflow: 'hidden',
                position: 'relative',
                background: PARCHMENT_BG,
                fontFamily: FONT,
            }}
        >
            {/* Outer border */}
            <div style={{
                position: 'absolute',
                inset: isLarge ? '0.22in' : '0.12in',
                border: '2.5px double #8b6320',
                pointerEvents: 'none',
                zIndex: 2,
            }}/>
            {/* Inner border */}
            <div style={{
                position: 'absolute',
                inset: isLarge ? '0.3in' : '0.18in',
                border: '1px solid #c4962a',
                pointerEvents: 'none',
                zIndex: 2,
            }}/>

            {/* Content */}
            <div style={{
                position: 'relative',
                zIndex: 3,
                padding: isLarge ? '0.45in 0.5in 0.38in 0.5in' : '0.25in 0.28in 0.2in 0.28in',
                height: '100%',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
            }}>
                <h1 style={{
                    textAlign: 'center',
                    fontSize: isLarge ? 26 : 17,
                    fontWeight: 'bold',
                    fontVariant: 'small-caps',
                    letterSpacing: isLarge ? 3 : 2,
                    color: '#2c1600',
                    margin: '0 0 2px 0',
                    lineHeight: 1.2,
                    textShadow: '1px 1px 1px rgba(0,0,0,0.15)',
                    fontFamily: FONT,
                }}>
                    {name}
                </h1>

                <div style={{
                    textAlign: 'center',
                    color: '#8b6320',
                    fontSize: isLarge ? 16 : 11,
                    margin: isLarge ? '2px 0 10px 0' : '1px 0 5px 0',
                    letterSpacing: 6,
                    lineHeight: 1,
                }}>
                    ✦ ❦ ✦
                </div>

                <div style={{
                    flex: 1,
                    overflow: 'hidden',
                    columns: isLarge ? '2' : '1',
                    columnGap: isLarge ? '0.45in' : undefined,
                    columnRule: isLarge ? '1px solid #c4962a' : undefined,
                    fontSize: isLarge ? 14 : 13,
                    lineHeight: 1.75,
                    color: '#1e0e00',
                    textAlign: 'justify',
                    hyphens: 'auto',
                }}>
                    {segments.map((seg, i) => {
                        if (seg.type === 'oog') {
                            return (
                                <div key={i} style={{
                                    background: 'rgba(255,255,255,0.55)',
                                    border: '1px solid #9a7030',
                                    borderLeft: '3px solid #8b6320',
                                    borderRadius: 2,
                                    padding: '4px 6px',
                                    margin: '0.06in 0',
                                    breakInside: 'avoid' as const,
                                    pageBreakInside: 'avoid' as any,
                                }}>
                                    <div style={{
                                        fontFamily: SANS,
                                        fontWeight: 'bold',
                                        fontSize: 7,
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.3,
                                        color: '#555',
                                        marginBottom: 3,
                                        whiteSpace: 'nowrap',
                                    }}>
                                        Out Of Game: the following effect targets the caster of the divination ritual
                                    </div>
                                    <div style={{
                                        fontFamily: SANS,
                                        fontSize: 9,
                                        color: '#1a1a1a',
                                        fontStyle: 'italic',
                                        lineHeight: 1.4,
                                    }}>
                                        {seg.content}
                                    </div>
                                </div>
                            )
                        }
                        return (
                            <p key={i} style={{
                                margin: '0 0 0.08in 0',
                                whiteSpace: 'pre-wrap',
                                textIndent: '0.15in',
                            }}>
                                {renderTextWithItalics(seg.content.trim())}
                            </p>
                        )
                    })}
                </div>

                <div style={{
                    textAlign: 'center',
                    color: '#8b6320',
                    fontSize: isLarge ? 14 : 10,
                    marginTop: 4,
                    letterSpacing: 4,
                    lineHeight: 1,
                }}>
                    ⸻ ✦ ⸻
                </div>
            </div>
        </div>
    )
}
